import { MutationCache, QueryClient } from '@tanstack/react-query';
import { ApiError } from '../lib/api-client';

const MAX_QUERY_RETRIES = 1;

/** Only transient failures (network, 5xx) are worth retrying; 4xx answers are final. */
function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_QUERY_RETRIES) {
    return false;
  }
  if (error instanceof ApiError) {
    return error.code === 'NETWORK_ERROR' || (error.status !== undefined && error.status >= 500);
  }
  return true;
}

export function createQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    mutationCache: new MutationCache({
      onError: (error) => {
        // A stale version means cached records are outdated: refetch so the user can retry.
        if (error instanceof ApiError && error.code === 'VERSION_CONFLICT') {
          void client.invalidateQueries();
        }
      },
    }),
    defaultOptions: {
      queries: { retry: shouldRetryQuery, refetchOnWindowFocus: true, staleTime: 10_000 },
      // Mutations are never retried automatically: binding actions must not be replayed silently.
      mutations: { retry: false },
    },
  });
  return client;
}
